import React from 'react'
import classNames from 'classnames/bind'
import { useUserSession } from 'hooks'
import { DateTime } from 'luxon'

import AddToCartButton from 'modules/buyer-hub/checkout/components/AddToCartButton'
import StockStatusBadge from '../StockStatusBadge'

import { ProductVarietyFragment } from './graphql/__generated__/ProductVarietyFragment'
import { ProductBadgeEnum } from '../../../../../../__generated__/globalTypes'

import styles from './ProductVarieties.module.css'

const cx = classNames.bind(styles)

const VarietyProperty = ({ label, children }: { label: string; children?: React.ReactNode }) => (
  <div className={styles.varietyProperty}>
    <span className={styles.propertyLabel}>{label}</span>
    <span className={styles.propertyContent}>{children}</span>
  </div>
)

type ProductVarietiesProps = {
  varieties: ProductVarietyFragment[]
  badge: ProductBadgeEnum | null
}

const ProductVarieties = ({ varieties, badge }: ProductVarietiesProps) => {
  const { isGuest, isBuyer } = useUserSession()
  const allowBackorders = varieties[0].catalog.createBackorders

  // This should never happen in practice, but there are soft-delete bugs which can cause a products varieties to be empty
  if (!varieties || varieties.length === 0) {
    return null
  }

  const { currencyCode, currencySymbol } = varieties[0].catalog

  return (
    <div className={styles.varietiesContainer}>
      {varieties.map(variety => {
        const inStock = !variety.outOfStock
        const stockDueDate = variety.stockDueDate ? DateTime.fromISO(variety.stockDueDate) : null

        return (
          <div key={variety.sku} className={styles.varietyProperties}>
            {varieties.length > 1 && (
              <div className={styles.varietyNameContainer}>
                <div className={styles.varietyName}>{variety.name}</div>

                <div className={styles.varietyBadges}>
                  <StockStatusBadge
                    inStock={inStock}
                    allowBackorders={allowBackorders}
                    stockDueDate={stockDueDate}
                    stockOnHand={variety.stockOnHand}
                    badge={badge}
                    hasMargin
                    showIcon
                  />
                </div>
              </div>
            )}

            {variety.hasPricingAccess && (
              <>
                <div className={styles.priceLabel}>Wholesale Price</div>
                {variety.price && (
                  <div className={styles.priceContainer}>
                    <div className={cx(styles.price, { strike: !!variety.salePrice })}>
                      {currencySymbol}
                      {variety.price.toFixed(2)}
                    </div>
                    {variety.salePrice && (
                      <div className={styles.salePrice}>
                        {currencySymbol}
                        {variety.salePrice.toFixed(2)}
                      </div>
                    )}
                    <span className={styles.priceCurrencyCode}>{currencyCode}</span>
                  </div>
                )}
                <div className={styles.priceUnit}>{variety.unit}</div>
              </>
            )}

            <VarietyProperty label="SKU">{variety.sku}</VarietyProperty>

            {/* Only show extended product information to logged in users. */}
            {!isGuest && (
              <>
                {variety.rrp && (
                  <VarietyProperty label="Retail price (RRP / MSRP)">
                    <span className={styles.currencyCode}>{currencyCode}</span> {currencySymbol}
                    {variety.rrp.toFixed(2)}
                  </VarietyProperty>
                )}

                {variety.minimumOrderQuantity && variety.minimumOrderQuantity > 1 && (
                  <VarietyProperty label="Minimum Quantity">{variety.minimumOrderQuantity}</VarietyProperty>
                )}

                {variety.boxQuantity && <VarietyProperty label="Box Quantity">{variety.boxQuantity}</VarietyProperty>}

                {variety.pricingTable != null && (variety.pricingTable?.length || 0) > 0 && (
                  <VarietyProperty label="Quantity Pricing">
                    <table className={styles.pricingTable}>
                      <tbody>
                        {variety.pricingTable.map(pricingTableRow => (
                          <tr className={styles.pricingTableRow} key={pricingTableRow.tierQty}>
                            <td className={styles.pricingTableCell}>{pricingTableRow.tierQty}+</td>
                            <td className={styles.pricingTableCell}>
                              {currencySymbol}
                              {pricingTableRow.unitPrice.toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </VarietyProperty>
                )}
              </>
            )}

            {isBuyer && variety.hasPricingAccess && (inStock || allowBackorders) && (
              <div className={styles.addToCartWrapper}>
                <div className={styles.addToCartLabel}>Order Quantity</div>
                <AddToCartButton
                  sellerId={variety.catalog.seller.id}
                  catalogId={variety.catalog.id}
                  productId={variety.productId}
                  variety={variety}
                />
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default ProductVarieties
